Copyright 2022 Bio-Conversion Databank Foundation Licensed under the Apache
License, Version 2.0(the "License"); you may not use this file except in
compliance with the License. You may obtain a copy of the License at
http://www.apache.org/licenses/LICENSE-2.0 Unless required by applicable law or
agreed to in writing, software distributed under the License is distributed on
an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express
or implied. See the License for the specific language governing permissions and
limitations under the License

<template>
  <div class="bg">
    <LoginCard v-if="!isLoading">
      <v-container class="my-12">
        <p>Login to your account</p>

        <v-form @submit.prevent="onLogin" v-model="isFormValid">
          <v-text-field
            v-model="email"
            label="Email"
            class="mx-2"
            data-testid="email"
            outlined
            clearable
          >
          </v-text-field>

          <v-text-field
            v-model="password"
            label="Password"
            class="mx-2"
            data-testid="password"
            outlined
            clearable
            :append-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append="() => (visible = !visible)"
            :type="visible ? 'text' : 'password'"
            :rules="[validateNotEmpty]"
          >
          </v-text-field>

          <v-alert v-if="error" color="error">
            {{ error }}
          </v-alert>

          <p align="right">
            <router-link
              :to="{ name: 'ForgotPassword' }"
              data-testid="forgot-password"
            >
              Forgot password?
            </router-link>
          </p>

          <v-btn
            elevation="6"
            large
            color="primary"
            type="submit"
            :disabled="!isFormValid || !isPayloadValid"
            data-testid="login"
          >
            Login
          </v-btn>

          <p class="my-4">
            <router-link
              :to="{ name: 'Register' }"
              data-testid="create-account"
            >
              Create account
            </router-link>
          </p>
        </v-form>
      </v-container>
    </LoginCard>
    <v-container v-else class="d-flex justify-center">
      <v-progress-circular indeterminate color="background">
      </v-progress-circular>
    </v-container>
  </div>
</template>

<script>
import { CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { encode, decode } from 'js-base64';
import nodeForge from 'node-forge';
import urlencode from 'urlencode';

import UserPool from '@/plugins/UserPool';
import { validateNotEmpty } from '@/utilities/validationRules';
import LoginCard from '@/components/login/LoginCard';

export default {
  name: 'DiscourseLogin',
  components: {
    LoginCard,
  },
  data: () => {
    return {
      // visual state
      visible: false,
      showLogin: true,
      isLoading: true,
      // login data
      email: '',
      password: '',
      isFormValid: false,
      // discourse data
      nonce: '',
      isPayloadValid: true,
      // error handling
      error: null,
    };
  },
  methods: {
    validateNotEmpty,
    onLogin() {
      const user = new CognitoUser({
        Username: this.email,
        Pool: UserPool,
      });

      const authDetails = new AuthenticationDetails({
        Username: this.email,
        Password: this.password,
      });

      user.authenticateUser(authDetails, {
        onSuccess: (data) => {
          console.log('Login sucess: ', data); // DEBUG
          localStorage.setItem('authenticated', 'true');
          this.postDiscourse(
            data.idToken.payload.email,
            data.idToken.payload['cognito:username'],
            data.idToken.payload.name,
            data.idToken.payload.picture,
          );
        },
        onFailure: (err) => {
          console.error('Login failed: ', err); // DEBUG
          this.error = err.message;
        },
        newPasswordRequired: (data) => {
          console.log('New password required: ', data); // DEBUG
          this.error = 'New password required.';
        },
      });
    },
    getDiscourse() {
      let sig = this.$route.query.sig;
      let payloadBASE64 = this.$route.query.sso;
      this.nonce = decode(payloadBASE64);
      // verify payload and signature
      let hmac = nodeForge.hmac.create();
      hmac.start('sha256', 'Xys4G({/+8V+s,m])-h?-)BwudG');
      hmac.update(payloadBASE64);
      let payloadSHA256 = hmac.digest().toHex();
      if (payloadSHA256 !== sig) {
        this.error = 'Payload is invalid';
        this.isPayloadValid = false;
      }

      // check if user is already logged in
      const authenticated = localStorage.getItem('authenticated');
      if (authenticated) {
        this.postDiscourse(
          this.$store.state.user.userAttributes.email,
          this.$store.state.user.userAttributes['cognito:username'],
          this.$store.state.user.userAttributes.name,
          this.$store.state.user.userAttributes.picture,
        );
      } else {
        this.isLoading = false;
      }
    },
    postDiscourse(email, external_id, name, avatar_url) {
      // create return payload and sig
      let payload =
        this.nonce +
        '&email=' +
        email +
        '&external_id=' +
        external_id +
        '&name=' +
        name +
        '&avatar_url=' +
        avatar_url +
        '&avatar_force_update=true';
      let payloadBASE64 = encode(payload);
      let payloadURL = urlencode(payloadBASE64);
      let sig = nodeForge.hmac.create();
      sig.start('sha256', process.env.VUE_APP_DISCOURSE_SECRET);
      sig.update(payloadBASE64);
      sig = sig.digest().toHex();

      let url =
        'https://discourse.bio-conversion.org' +
        '/session/sso_login?sso=' +
        payloadURL +
        '&sig=' +
        sig;

      window.location.href = url;
    },
  },
  created() {
    this.getDiscourse();
  },
};
</script>

<style scoped>
.bg {
  background-color: #0c5c7e;
  height: 100%;
}
</style>
